/**
 * headTrackedCamera — ENG-981 webcam head-tracking runtime.
 *
 * Module singleton (like `headFollowRuntime` / `movementComponent`): owns the
 * webcam/MediaPipe pipeline's permission + lifecycle state machine, its
 * config, and the pure raw-pose -> camera-local-offset mapping. This is NOT
 * React state — Task 4's `cameraController.apply()` reads `source` on the
 * render hot path every frame, so `source.target` is a preallocated 3-array
 * that is mutated in place and never reassigned.
 *
 * This module touches no DOM and imports no MediaPipe. The actual webcam +
 * FaceLandmarker plumbing lives in `headTrackerMediapipe.js` (created in a
 * later task) and is reached only through a lazy `import()` inside start() —
 * so a browser that never enables head tracking never downloads that code,
 * and a denied permission never triggers the ~13 MB wasm/model fetch (one
 * wasm variant — SIMD or non-SIMD, whichever the browser loads, ~9.5 MB —
 * plus the ~3.6 MB model; never both variants at once, so this is NOT the
 * ~19 MB on-disk size of public/mediapipe/, which holds both. The
 * tracker itself does getUserMedia before importing MediaPipe; this module
 * only defines the state contract around that ordering).
 *
 * State machine: idle -> armed -> starting -> tracking, with blocked/error
 * reachable from starting, AND error also reachable directly from tracking
 * (a mid-session detection failure — e.g. the device going away — is fatal to
 * that session exactly like an initial-handshake failure; see
 * headTrackerMediapipe.js's tick() -> onError). start() is retryable from
 * blocked/error (e.g. the viewport pill's retry button). `idle` means "no
 * REACHABLE headTrackedCamera node in the live graph" (see syncFromGraph) —
 * a node that's present but wired to nothing (the WIRE deleted, not the
 * node) is treated the same as absent; App.jsx's teardown watcher uses
 * execReachability.js's isNodeTypeReachable, not mere presence, precisely so
 * this claim holds (ENG-981 merge-gate review — a presence-only check missed
 * exactly the unwired-but-present case). `armed` means "enabled in the
 * graph, awaiting a user gesture or an already-granted permission".
 *
 * Never auto-prompts for camera permission: arm() only auto-starts when
 * `navigator.permissions.query({name:'camera'})` already reports 'granted' —
 * the one case where no prompt can appear. Any rejection, absence of the
 * Permissions API, or any other status (including Firefox, which doesn't
 * support the 'camera' permission name and rejects the query) leaves state at
 * 'armed', awaiting start() from an explicit user gesture.
 *
 * Graceful degradation, never silent fakery: a denied permission lands in
 * 'blocked'; any other start failure (initial or mid-session) lands in
 * 'error' with the real message. Losing the face mid-session (or
 * disabling/stopping) zeroes the offset target so the base camera is
 * unaffected — the smoothing/decay itself is cameraController's job (Task 4),
 * this module only ever writes the target.
 */

import { devLog } from './devLog.js'
import { cameraController } from './cameraController.js'

export const HEAD_TRACK_DEFAULTS = {
  enabled: false,
  mode: 'pan',
  scaleX: 1,
  scaleY: 1,
  scaleZ: 1,
  maxX: 0.3,
  maxY: 0.2,
  maxZ: 0.4,
  smoothing: 0.85,
  // Camera rotation toward the subject, in radians per metre of applied
  // offset (0 = pure translation). The munrocket/parallax-effect "pop":
  // their demo runs the equivalent of ~0.4 (0.12 rad at their 0.3 m full
  // deflection); 0.2 is Andres's tuned default from the 2026-08-13 feel
  // pass ("0.2 feels better than 0.4"). Applied by
  // cameraController._applyHeadOffset, composed AFTER lookAt like the
  // translation, so it can never compound across frames.
  swivel: 0.2,
  invertX: false,
  invertY: false,
  invertZ: false,
  trackFps: 24,
}

const CONFIG_KEYS = Object.keys(HEAD_TRACK_DEFAULTS)

/**
 * VIEW_MODE_PRESET — the config the tool-rail "parallax view mode" button
 * applies (startViewSession below). An EDITOR VIEW MODE, not blueprint
 * authoring: one click turns head-tracked parallax on with the values from
 * Andres's 2026-08-13 feel pass (scale 3 / max 0.8,0.5 / swivel 0.2 — the
 * amplified feel that reads clearly at typical orbit distances), the same
 * way a viewport perspective/ortho toggle carries its own settings rather
 * than reading them from the scene. Node-driven sessions are untouched by
 * this preset — it is only ever fed to configure() by startViewSession().
 */
export const VIEW_MODE_PRESET = {
  mode: 'pan',
  scaleX: 3,
  scaleY: 3,
  scaleZ: 1,
  maxX: 0.8,
  maxY: 0.5,
  maxZ: 0.4,
  smoothing: 0.85,
  swivel: 0.2,
  invertX: false,
  invertY: false,
  invertZ: false,
  trackFps: 24,
}

function clamp(v, min, max) {
  return v < min ? min : v > max ? max : v
}

/**
 * mapRawToOffset — pure raw camera-frame head pose -> camera-local offset.
 *
 * `raw`/`origin` are `[x,y,z]` metres in the tracker's camera frame (x =
 * image-right, y = up, z = toward the webcam, so a closer face has a LARGER
 * z, i.e. z moves toward 0 from a more-negative rest value). Writes into the
 * caller-owned `out` 3-array (never allocates, never reassigns — this is
 * called from the tracker's pose callback and read every render frame via
 * `source.target`).
 *
 * Default sign convention (ENG-981 plan, "window into the scene" effect, for
 * an UNMIRRORED webcam feed): the user's right hand is on image-left, so the
 * default mapping mirrors X (`out.x = -(dx)`) so that moving the user's head
 * to THEIR right moves the scene camera right. Y and Z pass through with
 * their natural sign (up stays up; closer -> dz > 0 -> camera moves forward).
 * `invertX/Y/Z` are the escape hatch if a real webcam pass (Task 9) finds a
 * default backwards for a given rig/webcam combination.
 */
export function mapRawToOffset(raw, origin, cfg, out) {
  const dx = raw[0] - origin[0]
  const dy = raw[1] - origin[1]
  const dz = raw[2] - origin[2]

  let x = -dx * cfg.scaleX
  let y = dy * cfg.scaleY
  let z = dz * cfg.scaleZ

  if (cfg.invertX) x = -x
  if (cfg.invertY) y = -y
  if (cfg.invertZ) z = -z

  out[0] = clamp(x, -cfg.maxX, cfg.maxX)
  out[1] = clamp(y, -cfg.maxY, cfg.maxY)
  out[2] = clamp(z, -cfg.maxZ, cfg.maxZ)
  return out
}

class HeadTrackedCamera {
  constructor() {
    this._resetForTest()
  }

  // ── config ────────────────────────────────────────────────────────────
  /** Merge a partial config patch (e.g. blueprint node `data`). Live — also
   *  mirrors mode/smoothing onto `source` so a config edit while tracking
   *  takes effect immediately, without waiting for a pose. */
  configure(data) {
    if (!data) return
    const prevMode = this.config.mode
    for (const key of CONFIG_KEYS) {
      if (data[key] !== undefined) this.config[key] = data[key]
    }
    // Change-guarded (configure re-runs on EVERY applied editor edit via the
    // onConstruct refire, most of which touch nothing here).
    if (this.config.mode !== prevMode) {
      devLog(`[head-track] mode ${prevMode} -> ${this.config.mode}`)
    }
    this.source.mode = this.config.mode
    this.source.smoothing = this.config.smoothing
    this.source.swivel = this.config.swivel
  }

  /**
   * syncFromGraph — declaratively derive runtime state from a node snapshot.
   * `node` is the `headTrackedCamera` node (or null when torn down/absent).
   * Idempotent: safe to call repeatedly whether or not anything changed.
   *
   * ENG-981 rework: this method's OWN contract is unchanged, but its caller
   * is not — it is no longer synced from mere graph presence. The only
   * callers now are EventRuntime.js's `headTrackedCamera` exec case (every
   * time the node's exec chain fires — see CameraNodes.jsx/NODES.md for how
   * a node gets wired to fire it) and App.jsx's teardown watcher
   * (`syncFromGraph(null)`, for the two cases an exec chain can't express:
   * the node being deleted outright, OR left present but UNWIRED — the
   * watcher checks reachability via execReachability.js's
   * isNodeTypeReachable, not mere presence, so both collapse to the same
   * "disarm" call here).
   */
  syncFromGraph(node) {
    // A view-mode session (tool-rail button, startViewSession) OWNS the
    // runtime while active: the graph neither configures nor tears it down.
    // Without this guard, the very next editor edit after clicking the rail
    // button would re-fire onConstruct (or App's teardown watcher, when no
    // node exists at all) and kill the session the user just started with an
    // explicit gesture. The graph contract resumes in full the moment the
    // session ends (stop()/disarm() clear the flag; the next notify re-syncs).
    if (this.viewSession) return
    if (!node) {
      this.stop()
      this._setState('idle')
      return
    }
    this.configure(node.data)
    if (node.data?.enabled) {
      this.arm()
    } else {
      this.disarm()
    }
  }

  /**
   * startViewSession() — the tool-rail "parallax view mode" button. An
   * editor view mode, independent of the blueprint: applies
   * VIEW_MODE_PRESET, marks the session so graph syncs leave it alone (see
   * syncFromGraph), and starts. MUST be called from a real user gesture —
   * same contract as the pill, and the only other caller of start() outside
   * it — since this is a path that can trigger the camera-permission prompt.
   * If a node-driven session is already tracking, the preset simply takes
   * over its config; the node re-asserts its own values on the next graph
   * sync after the view session ends.
   */
  startViewSession() {
    this.viewSession = true
    this.configure(VIEW_MODE_PRESET)
    if (this.state === 'idle') this._setState('armed')
    return this.start()
  }

  // ── lifecycle ─────────────────────────────────────────────────────────
  /**
   * arm() — the node is enabled in the graph. Never itself prompts for
   * permission. Returns a promise that settles once the (optional)
   * already-granted auto-start attempt has settled — callers may ignore it.
   *
   * Auto-start only fires on a genuine idle -> armed transition, NOT on
   * every call while already armed. syncFromGraph() is documented as safe
   * to call on every graph mutation, so if auto-start were wired to "being
   * armed" rather than "becoming armed", an unrelated graph edit after the
   * user explicitly stop()'d (which leaves state 'armed' by design — see
   * stop()) would silently turn the webcam back on with no gesture. The
   * same reasoning protects 'blocked'/'error': a call while in either must
   * be a full no-op, preserving the state AND errorMessage the pill is
   * showing — only an explicit start() (the user's retry click) may clear
   * it, never an incidental re-arm.
   */
  arm() {
    if (this.state !== 'idle') return Promise.resolve()
    this._setState('armed')
    return this._tryAutoStart()
  }

  /** disarm() — the node is present but disabled. Tears down any active
   *  tracker and goes fully idle (there is no separate "disabled" state). */
  disarm() {
    this.viewSession = false
    this._teardownTracker()
    this._setState('idle')
  }

  /**
   * start() — the user-gesture path (or an already-granted auto-start, or a
   * retry from 'blocked'/'error'). Lazily resolves the tracker factory so a
   * browser that never enables head tracking never imports MediaPipe, and
   * unit tests can inject a fake via `_setTrackerFactory` so the dynamic
   * import never executes under test.
   *
   * Race guard (whole-branch review, Critical): the `await import(...)`
   * below opens a window, before `this._tracker` is ever assigned, during
   * which `disarm()`/`stop()`/`_teardownTracker()` can run. Those paths find
   * `this._tracker` still null and are a complete no-op, so without a
   * separate signal this suspended continuation would have no way to learn
   * it was superseded — it would go on to build a tracker, call the real
   * `tracker.start()` (webcam + MediaPipe live), and either leak it (state
   * says idle/armed, tracker says otherwise) or get silently overwritten by
   * a later start(), orphaning the stream/timer/listener with no reachable
   * reference. `this._startToken` is a per-call identity: every teardown
   * path bumps it to a new value (see `_teardownTracker`, called by both
   * `disarm()` and `stop()`), and this method re-checks its own token after
   * every await — including the one
   * inside `tracker.start()` — stopping and discarding any tracker it
   * already built the moment it finds itself superseded.
   */
  async start() {
    if (this.state === 'starting' || this.state === 'tracking') return
    this._setState('starting')
    // Every session recentres fresh: without this, restarting after a
    // stop()/error would compare the new session's first pose against the
    // PREVIOUS session's origin, reporting a bogus offset before the user
    // has moved at all relative to their new resting position.
    this._origin = null
    this._lastRaw = null

    const token = (this._startToken = {})

    let factory
    try {
      // ENG-1107: the default tracker is a facade over the ONE shared webcam
      // session (webcamFaceSession.js) so the face tracker and this camera
      // never open two streams / two landmarkers. Same {start, stop} contract;
      // unit tests still inject their own factory via _setTrackerFactory.
      factory = this._trackerFactory ?? (async () => (await import('./webcamFaceSession.js')).webcamFaceSession.acquire())
    } catch (err) {
      if (this._startToken !== token) return // superseded before a tracker existed
      this._handleTrackerError(err)
      return
    }
    if (this._startToken !== token) return // superseded while resolving the factory — no tracker to stop

    // `await factory()` (not a bare call): production's factory is
    // synchronous, so this costs one microtask tick there, but it's what
    // lets a test factory be a deferred/async function — returning a promise
    // it resolves on its own schedule — so a test can interleave a
    // disarm()/stop() into the gap between "start begins" and "tracker
    // exists" that this whole guard exists to close. Without this, that gap
    // is only the dynamic import above, which every test bypasses via
    // `_setTrackerFactory` (that's WHY the race hid from the suite).
    const tracker = await factory()
    if (this._startToken !== token) {
      // Superseded between building and assigning the tracker: it was never
      // published to this._tracker, so no teardown path can reach it — stop
      // it directly rather than dropping the only reference to a live one.
      try {
        tracker.stop()
      } catch (err) {
        console.error('[head-track] tracker.stop() threw during a superseded start()', err)
      }
      return
    }
    this._tracker = tracker
    try {
      await tracker.start({
        trackFps: this.config.trackFps,
        onPose: (x, y, z) => this._onPose(x, y, z),
        onFaceLost: () => this._onFaceLost(),
        onError: (err) => this._handleTrackerError(err),
      })
    } catch (err) {
      if (this._startToken !== token) return // superseded — teardown already released this tracker
      this._handleTrackerError(err)
      return
    }
    if (this._startToken !== token) return // superseded while tracker.start() was in flight
    // A synchronous onError callback (the real tracker's denied-getUserMedia
    // path resolves rather than rejecting) may already have moved state past
    // 'starting' — only claim 'tracking' if nothing else has.
    if (this.state === 'starting') {
      this._setState('tracking')
    }
  }

  /** stop() — the viewport pill's "off switch" while tracking, or a generic
   *  teardown. Releases the tracker/stream and deactivates the source, but
   *  (unlike disarm()) returns to 'armed' rather than 'idle' — the node is
   *  still enabled in the graph, just not currently running. A no-op on
   *  state when called from 'idle' (nothing enabled to return to). */
  stop() {
    // Ends a view-mode session too (the pill's ⨯ and the rail toggle both
    // land here) — the graph contract resumes on the next sync.
    this.viewSession = false
    this._teardownTracker()
    if (this.state !== 'idle') this._setState('armed')
  }

  /** recenter() — re-zero the mapping at the most recent raw pose, so the
   *  user's current head position becomes the new "centre". If no pose has
   *  arrived yet (never started, or started but no frame processed), arm the
   *  *next* pose to become the origin instead of throwing — same as the
   *  first-pose-after-start behaviour. */
  recenter() {
    if (this._lastRaw) {
      this._origin = [this._lastRaw[0], this._lastRaw[1], this._lastRaw[2]]
      mapRawToOffset(this._lastRaw, this._origin, this.config, this.source.target)
    } else {
      this._origin = null
    }
  }

  // ── tracker callbacks (also reached via the CI test seams below — same
  //    handlers either way, so a headless e2e exercises the real code path) ──
  _onPose(x, y, z) {
    if (!this._lastRaw) this._lastRaw = [0, 0, 0]
    this._lastRaw[0] = x
    this._lastRaw[1] = y
    this._lastRaw[2] = z
    if (this._origin === null) {
      // First pose since start()/recenter(): establishes the origin, so the
      // user's resting head position maps to zero offset.
      this._origin = [x, y, z]
    }
    mapRawToOffset(this._lastRaw, this._origin, this.config, this.source.target)
    this.source.active = true
    // Notify ONLY on the false->true transition (a face re-entering frame).
    // This callback fires every tracked frame (~24-30fps per trackFps) while
    // a face IS seen, so an unconditional _notify() here would trigger a
    // React re-render every frame of tracking — do not "simplify" this to an
    // unconditional call. Regains are rare, so the guard keeps this cheap.
    const regained = !this.faceSeen
    this.faceSeen = true
    if (regained) this._notify()
  }

  _onFaceLost() {
    // No per-tick logging here on purpose — the tracker calls this once per
    // frame while the face stays lost, and that must never become log spam.
    // Same reasoning applies to _notify(): only fire on the true->false
    // transition (the face actually leaving frame), never per repeated
    // "still lost" callback — do not remove this guard, it's the mirror of
    // the one in _onPose and exists for the same per-frame-re-render reason.
    const lost = this.faceSeen
    this.faceSeen = false
    this._zeroTarget()
    // source.active is left untouched: cameraController owns the decay
    // (smooths the now-zero target back toward the base camera), which only
    // works if the source still reports active while the smoothing runs.
    if (lost) this._notify()
  }

  /** Zero source.target in place (never reassigns the array — it's the same
   *  object cameraController holds a reference to). Shared by every teardown
   *  path (face-lost, tracker error, stop/disarm) so "no residual offset"
   *  is enforced in exactly one place. */
  _zeroTarget() {
    this.source.target[0] = 0
    this.source.target[1] = 0
    this.source.target[2] = 0
  }

  /** Stop and release the current tracker, if any (idempotent — safe to
   *  call with no active tracker). Shared by every path that ends a
   *  session, so a mid-session onError (device lost, runtime failure — not
   *  just an initial-handshake failure) can never leak the tracker/stream:
   *  before this fix, _handleTrackerError nulled the reference without
   *  calling stop(), which would have left a live MediaStream (camera light
   *  on) once Task 3's real tracker exists. */
  _releaseTracker() {
    if (this._tracker) {
      try {
        this._tracker.stop()
      } catch (err) {
        console.error('[head-track] tracker.stop() threw during teardown', err)
      }
      this._tracker = null
    }
  }

  _handleTrackerError(err) {
    this._releaseTracker()
    this.source.active = false
    this._zeroTarget()
    this.faceSeen = false
    if (err?.name === 'NotAllowedError') {
      this._setState('blocked')
    } else {
      this.errorMessage = err?.message || String(err ?? 'unknown error')
      this._setState('error')
    }
  }

  /** Called by both disarm() and stop() — the two paths that end a session
   *  from the OUTSIDE of start()'s own continuation. Invalidating the start
   *  token here (rather than only inside start() itself) is what lets a
   *  disarm()/stop() that lands WHILE a start() is still suspended on an
   *  await — before this._tracker is even assigned — be detected by that
   *  suspended continuation the moment it wakes back up. See start()'s
   *  header for the full race this closes. */
  _teardownTracker() {
    this._startToken = null
    this._releaseTracker()
    this.source.active = false
    this._zeroTarget()
    this.faceSeen = false
  }

  _tryAutoStart() {
    if (typeof navigator === 'undefined' || !navigator.permissions?.query) return Promise.resolve()
    let queryResult
    try {
      queryResult = navigator.permissions.query({ name: 'camera' })
    } catch {
      // Some engines throw synchronously for an unsupported permission name.
      return Promise.resolve()
    }
    if (!queryResult || typeof queryResult.then !== 'function') return Promise.resolve()
    return queryResult.then(
      (status) => {
        // Re-check state: arm() may have been superseded (disarm/stop) while
        // the query was in flight.
        if (status?.state === 'granted' && this.state === 'armed') {
          return this.start()
        }
      },
      () => {
        // Rejected (Firefox doesn't support the 'camera' permission name) —
        // stay armed, awaiting an explicit user gesture. Not an error.
      },
    )
  }

  _setState(next) {
    if (this.state === next) return
    this.state = next
    if (next !== 'error') this.errorMessage = null
    devLog('[head-track] state ->', next)
    this._notify()
  }

  _notify() {
    for (const fn of this._listeners) {
      try {
        fn(this.state)
      } catch (err) {
        console.error('[head-track] onStateChange listener threw', err)
      }
    }
  }

  /** onStateChange(cb) — cb(state) fires on every state transition. Returns
   *  an unsubscribe function. */
  onStateChange(cb) {
    this._listeners.add(cb)
    return () => this._listeners.delete(cb)
  }

  // ── test seams ────────────────────────────────────────────────────────
  /** Inject a fake tracker factory: `() => ({ start(opts), stop() })`. Used
   *  by unit tests (always) so the real dynamic import of
   *  headTrackerMediapipe.js never executes under test, and available to the
   *  app for any future non-MediaPipe tracker. Pass null to restore the
   *  lazy-import default. */
  _setTrackerFactory(fn) {
    this._trackerFactory = fn
  }

  /** _injectPose / _injectFaceLost — CI e2e seams (Task 7): drive tracking
   *  with no webcam and no MediaPipe by routing through the SAME internal
   *  handlers the real tracker calls. Work whether or not a tracker is
   *  currently running (e.g. from 'armed', before any start()) — they only
   *  update the mapping/source state, they don't require or imply a live
   *  session. */
  _injectPose(x, y, z) {
    this._onPose(x, y, z)
  }

  _injectFaceLost() {
    this._onFaceLost()
  }

  /** Test-only: reset every field to its fresh-singleton value. Not part of
   *  the app's runtime contract — used to isolate unit tests from each other
   *  against the one shared module singleton. */
  _resetForTest() {
    this.state = 'idle'
    this.faceSeen = false
    this.errorMessage = null
    this.viewSession = false
    this.config = { ...HEAD_TRACK_DEFAULTS }
    this.source = {
      target: [0, 0, 0],
      mode: HEAD_TRACK_DEFAULTS.mode,
      smoothing: HEAD_TRACK_DEFAULTS.smoothing,
      swivel: HEAD_TRACK_DEFAULTS.swivel,
      active: false,
    }
    this._origin = null
    this._lastRaw = null
    this._tracker = null
    this._trackerFactory = null
    this._startToken = null
    this._listeners = new Set()
  }
}

export const headTrackedCamera = new HeadTrackedCamera()

// Step 5 (ENG-981 Task 4): wire this module's `source` into the camera
// controller's additive head-offset stage. Dependency direction is
// feature -> controller (never the reverse) — cameraController.js knows
// nothing about headTrackedCamera or MediaPipe, it only reads the `source`
// object it's handed here. `source` is never reassigned by this module (see
// the class header), so passing it once at import time is sufficient; the
// controller reads the same array/object every frame that _onPose /
// _onFaceLost / _zeroTarget mutate in place.
cameraController.setHeadOffsetSource(headTrackedCamera.source)

if (typeof window !== 'undefined') {
  window.__HEAD_TRACK__ = headTrackedCamera
}
